import { Box, IconButton, Palette, Stack, Typography } from "@mui/material";
import { BAR_ANIMATION_DELAY_STEP_MS, BAR_COUNT } from "@pages/askDost/utils/constants";
import { CircleX, Pause, Play } from "lucide-react";
import { useEffect, useState } from "react";
import {
  dictationContainerSx,
  transcriptTextSx,
  recordingDotSx,
  elapsedTimeSx,
  barSx,
  pauseButtonSx,
  cancelButtonSx,
  confirmButtonSx,
} from "../../styles/AskDostDictationIndicatorStyle";

interface IAskDostDictationIndicatorProps {
  palette: Palette;
  translate: (text: string, options?: any) => string;
  onConfirm: () => void;
  onCancel: () => void;
  liveTranscript: string;
  isPaused: boolean;
  onPauseToggle: () => void;
}

const roundToEven = (value: number) => Math.round(value / 2) * 2;

const BAR_HEIGHTS = Array.from({ length: BAR_COUNT }, (_, index) =>
  roundToEven(8 + 16 * Math.abs(Math.sin(index * 1.27))),
);

const formatElapsed = (totalSeconds: number) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
    2,
    "0",
  )}`;
};

export const AskDostDictationIndicator: React.FC<
  IAskDostDictationIndicatorProps
> = ({
  palette,
  translate,
  onConfirm,
  onCancel,
  liveTranscript,
  isPaused,
  onPauseToggle,
}) => {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => setElapsedSeconds((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, [isPaused]);

  const renderWaveformBars = () =>
    BAR_HEIGHTS.map((height, index) => (
      <Box
        key={index}
        sx={barSx(
          palette,
          isPaused,
          height,
          index * BAR_ANIMATION_DELAY_STEP_MS,
        )}
      />
    ));

  const renderConfirmIcon = () => (
    <Box
      component="svg"
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke={palette.primary.contrastText}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 6L9 17l-5-5" />
    </Box>
  );

  return (
    <Stack spacing={2} sx={dictationContainerSx}>
      <Typography variant="body2" sx={transcriptTextSx(palette, !!liveTranscript)}>
        {liveTranscript
          ? liveTranscript
          : translate(isPaused ? "paused" : "listening")}
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center">
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ flexShrink: 0 }}
        >
          <Box sx={recordingDotSx(palette, isPaused)} />
          <Typography variant="caption" sx={elapsedTimeSx(palette)}>
            {formatElapsed(elapsedSeconds)}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          spacing="2px"
          alignItems="center"
          sx={{ flex: 1, height: 26, overflow: "hidden" }}
        >
          {renderWaveformBars()}
        </Stack>

        <IconButton
          size="small"
          onClick={onPauseToggle}
          title={translate(isPaused ? "resume" : "pause")}
          sx={pauseButtonSx(palette)}
        >
          {isPaused ? (
            <Play size={16} color={palette.primary.main} />
          ) : (
            <Pause size={16} color={palette.text.primary} />
          )}
        </IconButton>

        <IconButton
          size="small"
          onClick={onCancel}
          title={translate("cancel")}
          sx={cancelButtonSx(palette)}
        >
          <CircleX size={16} color={palette.error.dark} />
        </IconButton>

        <IconButton
          size="small"
          onClick={onConfirm}
          title={translate("done")}
          sx={confirmButtonSx(palette)}
        >
          {renderConfirmIcon()}
        </IconButton>
      </Stack>
    </Stack>
  );
};
